import React, { useEffect, useMemo, useState } from 'react';
import {
  Divider,
  Stack,
  Box,
  Typography as Muitypo,
  Card,
  Grid,
  ButtonGroup,
  IconButton,
  Skeleton,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  Button,
  Label,
  Link,
  Tabs,
  TextField,
  Typography,
  Chip,
  DatePicker,
  Pagination,
  Center,
} from '../../components';

import VehicleCompare from '../VehicleCompare';
import { GetAssignmentRes } from '../../types/assignments';
import { cancelAssignment, getAssignments, rejectAssignment, retryOnHoldAssignment } from '../../services/assignment';
import { LoadingDialogue } from '../../components/Spinners';
import useConfirm from '../../hooks/useConfirm';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { GLOBAL_USER } from '../../utils/constants';

interface TRequests extends GetAssignmentRes {
  decision?: 'Cancelled' | 'Rejected';
}

type RequestStatus = 'pending' | 'approved' | 'rejected' | 'on hold';

interface StatusBtn {
  label: string;
  type: RequestStatus;
}

const Loader = ({ qty = 7 }) => {
  return (
    <Grid container p={1}>
      <Grid xs={5}>
        {Array.from({ length: qty }).map(() => (
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ my: 1, borderRadius: 2 }} />
        ))}
      </Grid>
      <Grid xs={7} pl={2}>
        <Skeleton variant="rectangular" width="100%" height={500} sx={{ my: 1, borderRadius: 2 }} />
      </Grid>
    </Grid>
  );
};

const OnHoldAssignmentGrid = () => {
  const { confirm } = useConfirm();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();
  const [selectedStatus, setSelectedStatus] = useState<RequestStatus>('on hold');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = React.useState(1);
  const [processingRequest, setProcessingRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [requests, setRequests] = useState<TRequests[]>([]);
  const [selected, setRequestSelected] = useState(0);
  const [decisionPending, setDecisionPending] = useState(false);
  const [transactionErrors, setTransactionErrors] = useState<any>();
  let reason = '';

  useEffect(() => {
    if (!(startDate && endDate) && requests.length !== 0) {
      return;
    }
    setLoading(true);
    getAssignments({
      // requestor: GLOBAL_USER,
      status: 'On Hold',
      fromDate: startDate?.toISOString() || '',
      toDate: endDate?.toISOString() || '',
    })
      .then((res) => setRequests(res))
      .catch()
      .finally(() => setLoading(false));
  }, [endDate]);

  const optRequests = useMemo(() => {
    return requests.filter((r) => r.status.toLowerCase() === selectedStatus);
  }, [requests, selectedStatus]);

  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return optRequests.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, optRequests]);

  const requestSelected = paginated[selected];

  const onChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const getVariant = (p: RequestStatus) => {
    if (p === selectedStatus) {
      return 'contained';
    }
    return 'outlined';
  };

  const handleChangeReason = (nr: string) => {
    reason = nr;
  };

  const handleChangePage = (newPage: number) => {
    setRequestSelected(0);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (n: number) => {
    setRequestSelected(0);
    setRowsPerPage(n);
    setPage(1);
  };

  const ReasonInput = (
    <Box my={1}>
      <Muitypo variant="caption">Add Reason</Muitypo>
      <TextField
        sx={{ bg: (theme) => theme.palette.common.white }}
        placeholder="Reason..."
        onChange={(e) => handleChangeReason(e.target.value)}
      />
    </Box>
  );

  const CancelRequestBody = (
    <Stack py={1} spacing={2}>
      <Box
        p={1}
        mx={1}
        sx={{ color: '#c3493b', display: 'flex', border: '1px solid #c3493b', borderRadius: 2, alignItems: 'center' }}
      >
        <Icon icon="charm:circle-warning" color="#c3493b" />
        <Muitypo variant="caption" ml={1}>
          Vehicle will be available in the inventory once it is cancelled.
        </Muitypo>
      </Box>
      <Stack>
        <Muitypo variant="body1">VIN</Muitypo>
        <Muitypo variant="caption">{requestSelected?.inventoryVehicle.vin || 'N/A'}</Muitypo>
      </Stack>
    </Stack>
  );

  const onReject = async () => {
    const isConfirmed = await confirm({
      title: 'Reject Assignment',
      description: 'Are you sure you want to reject this request? You can view this request in your reject list.',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Reject',
      confirmBody: ReasonInput,
    });
    if (isConfirmed) {
      setDecisionPending(true);
      try {
        if (!reason) {
          showErrorSnackbar({ message: 'Please provide reason for rejection.' });
          return;
        }
        const data = {
          requestId: requestSelected?.requestNumber,
          reason,
          requestor: GLOBAL_USER,
        };
        const rejectAssignmentResp = await rejectAssignment(data);
        // const idx = requests.findIndex((a) => a.requestNumber === data.requestId);
        // if (idx !== -1) {
        //   const newItem = requests[idx];
        //   newItem.decision = 'Rejected';
        //   setRequests((prev) => {
        //     prev[idx] = newItem;
        //     return prev;
        //   });

        setRequests((prev) => {
          const rmd = prev.filter((a) => a.requestNumber !== data.requestId);
          return rmd;
        });
        setRequestSelected(0);
        if (rejectAssignmentResp.message !== 'Success') {
          showErrorSnackbar({
            message: rejectAssignmentResp.message,
          });
        } else {
          showSuccessSnackbar({
            message: `The request ${requestSelected.requestNumber} has been rejected successfully.`,
          });
        }
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      } finally {
        setDecisionPending(false);
        reason = '';
      }
    } else {
      reason = '';
    }
  };

  const retryAssignment = async () => {
    setDecisionPending(true);

    try {
      const requestId = requestSelected?.requestNumber || '';
      const res = await retryOnHoldAssignment(requestId, GLOBAL_USER, true);

      if (res.message === 'Success') {
        setDecisionPending(false);
        setRequests((prev) => {
          const rmd = prev.filter((a) => a.requestNumber !== requestId);
          return rmd;
        });
        showSuccessSnackbar({
          message: `The request ${requestSelected?.requestNumber} has been approved successfully.`,
        });
      } else if (res?.sdfcResponse?.data || res?.vmsResponse?.data) {
        setDecisionPending(false);
        setTransactionErrors(res);
        // showErrorSnackbar({ message: res.message });
      } else {
        setDecisionPending(false);
        showErrorSnackbar({ message: res.message });
      }
    } catch (error) {
      showErrorSnackbar({ message: 'Something went wrong. Try again.' });
    }
  };

  const customerDisplayName = (pg: any) => {
    if (pg?.customerName && !pg?.customerName.includes('undefined')) {
      return pg?.customerName;
    }
    return 'N/A';
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <LoadingDialogue open={decisionPending} />
      <Box p={0.5} mt={1}>
        <Grid container>
          <Grid item xs={5}>
            <Box my={1} display="flex" justifyContent="flex-end">
              <DatePicker
                selected={startDate}
                onChange={onChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                placeholderText="Select Date"
              />
            </Box>
            <Box
              sx={{
                p: (theme) => theme.spacing(1, 0, 3, 0),
                overflowY: 'auto',
                height: 'auto',
              }}
            >
              {paginated?.length ? (
                <>
                  {paginated.map((pg, idx) => (
                    <Card
                      key={pg.requestNumber}
                      elevation={2}
                      variant="outlined"
                      sx={{
                        my: 1,
                        cursor: 'pointer',
                        padding: '15px 15px 15px 15px',
                        borderColor: (theme) =>
                          pg?.requestNumber === requestSelected?.requestNumber ? theme.palette.primary.main : null,
                        backgroundColor: () => (pg.requestNumber === requestSelected.requestNumber ? '#eee' : '#fff'),
                      }}
                      onClick={() => setRequestSelected(idx)}
                    >
                      <Grid container>
                        <Grid item xs={12} md={2} lg={2}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              REQUEST NUMBER
                            </Typography>
                            <Typography weight={400} size={14}>
                              {pg.requestNumber}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={2} lg={2.5}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              {pg.inventoryVehicle.vin ? 'VIN' : 'BATCH NUMBER'}
                            </Typography>
                            <Typography weight={400} size={14} sx={{ width: '100%', wordWrap: 'break-word' }}>
                              {pg.inventoryVehicle.vin ? pg.inventoryVehicle.vin : pg.inventoryVehicle.batchNumber}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={2} lg={2}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              CUSTOMER NAME
                            </Typography>
                            <Typography weight={400} size={14} color="#3E3E3E">
                              {customerDisplayName(pg)}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={2} lg={2}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              REQUESTED DATE
                            </Typography>
                            <Typography weight={400} size={14} color="#3E3E3E">
                              {pg.requestDate}
                            </Typography>
                          </Stack>
                        </Grid>

                        {(selectedStatus === 'pending' || selectedStatus === 'on hold') && (
                          <Grid item xs={12} md={2} lg={2.5}>
                            <Stack
                              alignItems="start"
                              justifyContent="center"
                              sx={{ height: '100%', width: '100%', textWrap: 'wrap' }}
                            >
                              <Typography weight={700} size={10} color="#3e3e3e">
                                REQUESTED BY
                              </Typography>
                              <Typography
                                weight={400}
                                size={14}
                                color="#3E3E3E"
                                sx={{ width: '100%', wordWrap: 'break-word' }}
                              >
                                {pg.requestedBy}
                              </Typography>
                            </Stack>
                          </Grid>
                        )}

                        {selectedStatus === 'approved' && (
                          <Grid item xs={12} md={4} lg={2.5}>
                            <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                              <Typography weight={700} size={10} color="#3e3e3e">
                                APPROVED DATE
                              </Typography>
                              <Typography weight={400} size={14} color="#3E3E3E">
                                {pg.modifiedDate}
                              </Typography>
                            </Stack>
                          </Grid>
                        )}

                        {selectedStatus === 'rejected' && (
                          <Grid item xs={12} md={4} lg={2.5}>
                            <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                              <Typography weight={700} size={10} color="#3e3e3e">
                                REJECTED DATE
                              </Typography>
                              <Typography weight={400} size={14} color="#3E3E3E">
                                {pg.modifiedDate}
                              </Typography>
                            </Stack>
                          </Grid>
                        )}
                        <Grid lg={1}>
                          <Stack direction="row" alignItems="center" justifyContent="end" sx={{ height: '100%' }}>
                            {(pg?.transactionErrors?.requestId || transactionErrors?.requestId) &&
                              (transactionErrors?.sfdcResponse?.data ||
                                transactionErrors?.vmsResponse?.data ||
                                pg?.transactionErrors?.sfdcResponse?.data ||
                                pg?.transactionErrors?.vmsResponse?.data) && (
                                <Icon icon="mdi:flag" width="20px" height="20px" color="#E04732" />
                              )}
                            <IconButton onClick={() => setRequestSelected(idx)}>
                              <Icon icon="akar-icons:chevron-right" fontSize={20} color="#456BD9" />
                            </IconButton>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </>
              ) : (
                <Box p={0.5} mt={1}>
                  <Center>
                    <Muitypo variant="body1">No requests found.</Muitypo>
                  </Center>
                </Box>
              )}
            </Box>
            {!!paginated.length && (
              <Box>
                <Pagination
                  count={optRequests.length}
                  page={page}
                  onItemsPerPageChange={handleChangeRowsPerPage}
                  onPageChange={handleChangePage}
                  itemsPerPage={rowsPerPage}
                  countOpts={[10, 20, 30, 50]}
                />
              </Box>
            )}
          </Grid>
          {requestSelected && (
            <Grid xs={7}>
              <Box
                p={(theme) => theme.spacing(2.5, 4.6, 3, 4.6)}
                m={(theme) => theme.spacing(1, 1, 0, 2.6)}
                borderRadius={1.5}
                bgcolor={(theme) => theme.palette.grey[50]}
                border={(theme) => `1px solid #E3E3E3`}
              >
                <Stack direction="row" alignItems="flex-end">
                  <Stack direction="row" justifyContent="space-between" width="100%">
                    <Muitypo variant="h4" fontWeight={500} fontSize={32} color="#1F1F1F">
                      {requestSelected.requestNumber}
                    </Muitypo>
                    {(selectedStatus === 'pending' || selectedStatus === 'on hold') && (
                      <Box alignItems="flex-end">
                        <Button
                          variant="outlined"
                          onClick={onReject}
                          disabled={
                            processingRequest ||
                            !!requestSelected.decision ||
                            !(
                              requestSelected?.transactionErrors?.sfdcResponse?.statusCode !== 200 &&
                              requestSelected?.transactionErrors?.vmsResponse?.statusCode !== 200
                            )
                          }
                        >
                          {requestSelected.decision || 'Reject Request'}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Stack>
                <Box my={1}>
                  {requestSelected.comments && (
                    <Stack sx={{ bgcolor: '#f2f2f2', p: 1, borderRadius: 1 }}>
                      <Muitypo variant="caption" sx={{ fontSize: 10, color: (theme) => theme.palette.grey[600] }}>
                        Reason for request
                      </Muitypo>
                      <Muitypo variant="caption">{requestSelected.comments}</Muitypo>
                    </Stack>
                  )}
                </Box>
                <VehicleCompare
                  customerVehicle={requestSelected?.customerVehicle}
                  inventoryVehicle={requestSelected?.inventoryVehicle}
                  showDifferences={false}
                  transactionErrors={transactionErrors || requestSelected?.transactionErrors}
                  retryCall={retryAssignment}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default OnHoldAssignmentGrid;
